// src/contexts/UserContext.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext"; // Assuming you have an AuthContext to get the current user
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase"; // Assuming you have initialized Firestore in a firebase.ts file
import { User } from "../models/User";

// Define the UserContext type
interface UserContextType {
  user: User;
  loading: boolean;
  error: string | null;
}

// Create the context
const UserContext = createContext<UserContextType | undefined>(undefined);

// Create a provider component
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { user: currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser) {
      setUser(null);
      setLoading(false);
      return;
    }
    const docRef = doc(db, "users", currentUser.uid);
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        setLoading(false);
        if (docSnapshot.exists()) {
          setUser(docSnapshot.data());
        } else {
          setError("User not found");
        }
      },
      (err) => {
        setError(`Failed to listen to user changes: ${err.message}`);
      },
    );
    return () => {
      unsubscribe();
    };
  }, [currentUser?.uid]);

  return (
    <UserContext.Provider value={{ user, loading, error }}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook for using the UserContext
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within an UserProvider");
  }
  return context;
};
